import React, { useMemo } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import { convertToBrazilDate } from "../../../helpers/date";
import { Navbar } from "../../landingPage/components";
import ClipLoader from "react-spinners/ClipLoader";
import useBlogPost from "../../../hooks/useBlogPost";
import { useReadingTime } from "../../../helpers/readingTime";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./article.css";
import { TagList } from "../components/TagList";
import { PostCard } from "../components/PostCard";

const BackButton = () => (
  <a
    href="/blog"
    className="absolute -left-32 top-6 hidden lg:inline-flex items-center gap-2 text-gray-300 hover:text-brandpink transition-colors font-bold"
  >
    <ArrowLeftIcon className="w-6 h-6" />
    <span className="text-base">Voltar</span>
  </a>
);

const ArticleHeader = ({ title, date, readingTime }) => (
  <header className="flex flex-col gap-2 mb-2">
    <h1 className="font-bold text-brandpink text-4xl text-center uppercase">
      {title}
    </h1>
    <div className="flex flex-col items-center md:items-start gap-1">
      <div className="flex items-center gap-2">
        <CalendarIcon className="size-5 text-gray-400" />
        <span className="text-gray-400 font-bold">
          {convertToBrazilDate(date)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <ClockIcon className="size-5 text-gray-400" />
        <span className="text-gray-400 font-bold">
          {readingTime} min de leitura
        </span>
      </div>
    </div>
  </header>
);

const ArticleFooter = ({ nextPost, prevPost }) => (
  <div className="mt-8 px-4 pt-2 border-t-2 border-gray-600">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-1 order-2 md:order-none bg-gray-700/30 p-3 rounded-lg md:bg-transparent md:p-0">
        {prevPost && (
          <div className="flex flex-col gap-2">
            <a
              href={`/blog/post/${prevPost.id}`}
              className="text-gray-300 hover:text-brandpink text-sm md:flex md:items-center md:gap-1 text-center md:text-left transition-colors"
            >
              <span className="hidden md:inline">
                <ArrowLeftIcon className="w-4 h-4" />
              </span>
              <span className="font-bold">Post anterior</span>
            </a>
            <PostCard
              post={prevPost}
              compact
              full
              showBorder={false}
              alwaysClickable
            />
          </div>
        )}
      </div>
      <div className="md:col-span-1 order-1 md:order-none bg-gray-700/30 p-3 rounded-lg md:bg-transparent md:p-0">
        {nextPost && (
          <div className="flex flex-col gap-2">
            <a
              href={`/blog/post/${nextPost.id}`}
              className="text-gray-300 hover:text-brandpink text-sm md:flex md:items-center md:gap-1 md:justify-end text-center md:text-right transition-colors"
            >
              <span className="font-bold">Próximo post</span>
              <span className="hidden md:inline">
                <ArrowRightIcon className="w-4 h-4" />
              </span>
            </a>
            <PostCard
              post={nextPost}
              compact
              full
              showBorder={false}
              alwaysClickable
            />
          </div>
        )}
      </div>
    </div>
  </div>
);
export default function Article() {
  const { id } = useParams();
  const {
    blogPost: article,
    nextPost,
    prevPost,
    loading,
  } = useBlogPost(id, { getAdjacent: true });

  const processedContent = useMemo(() => {
    if (!article?.content) return "";
    const tempQuill = new Quill(document.createElement("div"), {
      theme: "snow",
      readOnly: true,
    });
    tempQuill.setContents(article.content);
    return tempQuill.root.innerHTML;
  }, [article?.content]);

  const readingTime = useReadingTime(processedContent);

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <ClipLoader color="#ce154e" loading={loading} size={50} />
      </div>
    );
  if (!article) return <div>Article not found.</div>;

  return (
    <>
      <Navbar />
      <main className="w-full flex flex-col items-center mt-4">
        <div className="relative w-full sm:w-[80%] md:w-[70%] xl:w-[45%]">
          <BackButton />
          <article className="w-full bg-deep-space rounded-xl py-4">
            <ArticleHeader
              title={article.title}
              date={article.published_at}
              readingTime={readingTime}
            />
            <div
              className="prose ql-editor"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />
            <div className="px-4">
              <TagList tags={article.tags} />
            </div>
            <ArticleFooter nextPost={nextPost} prevPost={prevPost} />
          </article>
        </div>
      </main>
    </>
  );
}
